/**
 * AdminMfaResetPage — /admin/mfa/reset
 *
 * Public page. Two sub-pages:
 *   /admin/mfa/reset            — Request reset (email input)
 *   /admin/mfa/reset/confirm/:token — Consume reset token from email link
 *
 * Generic response copy prevents account-existence enumeration.
 * Design: matches AdminLoginPage — clinical-charcoal bg, amber accent, white card.
 */

import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '@/lib/api';

// ---------------------------------------------------------------------------
// Icons
// ---------------------------------------------------------------------------

function ShieldIcon() {
  return (
    <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Request reset form
// ---------------------------------------------------------------------------

function RequestResetForm() {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    setIsSubmitting(true);
    setError('');

    try {
      await api.post('/admin/auth/mfa/request-reset', { email: email.trim() });
      setSubmitted(true);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Something went wrong. Please try again.';
      setError(msg);
    } finally {
      setIsSubmitting(false);
    }
  }, [email]);

  if (submitted) {
    return (
      <div className="text-center">
        <div className="flex items-center justify-center w-12 h-12 rounded-full bg-green-100 mx-auto mb-4">
          <svg className="w-6 h-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
          </svg>
        </div>
        <h2 className="font-heading text-lg font-semibold text-clinical-charcoal mb-2">Check your email</h2>
        <p className="text-sm font-body text-clinical-text-muted">
          If that email address is on file, a reset link has been sent. The link expires in 30 minutes.
        </p>
      </div>
    );
  }

  return (
    <div>
      <h2 className="font-heading text-xl font-semibold text-clinical-charcoal mb-2">Reset two-factor authentication</h2>
      <p className="text-sm font-body text-clinical-text-muted mb-6">
        Enter your admin email address. If it is on file, we will send a one-time reset link.
      </p>

      <form onSubmit={(e) => { void handleSubmit(e); }} className="space-y-4">
        <div>
          <label htmlFor="reset-email" className="form-label">Email Address</label>
          <input
            id="reset-email"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => { setEmail(e.target.value); setError(''); }}
            className="form-input"
            placeholder="admin@example.com"
            required
            autoFocus
          />
        </div>

        {error && (
          <div className="rounded-clinical border border-red-200 bg-red-50 px-4 py-3">
            <p className="text-sm font-body text-red-700">{error}</p>
          </div>
        )}

        <button
          type="submit"
          disabled={isSubmitting || !email.trim()}
          className="btn-primary w-full"
        >
          {isSubmitting ? 'Sending…' : 'Send reset link'}
        </button>
      </form>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Confirm reset (token from URL param)
// ---------------------------------------------------------------------------

function ConfirmResetForm({ token }: { token: string }) {
  const navigate = useNavigate();
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [error, setError] = useState('');

  useEffect(() => {
    if (!token) return;
    setStatus('loading');
    api.post('/admin/auth/mfa/complete-reset', { token })
      .then(() => setStatus('success'))
      .catch((err: unknown) => {
        const msg = err instanceof Error ? err.message : 'This reset link is invalid or has expired.';
        setError(msg);
        setStatus('error');
      });
  }, [token]);

  if (status === 'loading') {
    return (
      <div className="flex flex-col items-center gap-4 py-4">
        <div className="w-8 h-8 border-2 border-clinical-amber border-t-transparent rounded-full animate-spin" />
        <p className="text-sm font-body text-clinical-text-muted">Verifying reset link…</p>
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div className="text-center">
        <div className="flex items-center justify-center w-12 h-12 rounded-full bg-green-100 mx-auto mb-4">
          <svg className="w-6 h-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
          </svg>
        </div>
        <h2 className="font-heading text-lg font-semibold text-clinical-charcoal mb-2">
          Two-factor authentication cleared
        </h2>
        <p className="text-sm font-body text-clinical-text-muted mb-6">
          Your 2FA enrollment has been removed. You will be prompted to set up a new authenticator on your next login.
        </p>
        <button
          type="button"
          onClick={() => void navigate('/admin/login', { replace: true })}
          className="btn-primary w-full"
        >
          Return to login
        </button>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="text-center">
        <div className="flex items-center justify-center w-12 h-12 rounded-full bg-red-100 mx-auto mb-4">
          <svg className="w-6 h-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </div>
        <h2 className="font-heading text-lg font-semibold text-clinical-charcoal mb-2">Reset link invalid</h2>
        <p className="text-sm font-body text-clinical-text-muted mb-4">{error}</p>
        <button
          type="button"
          onClick={() => void navigate('/admin/mfa/reset', { replace: true })}
          className="btn-primary w-full"
        >
          Request a new reset link
        </button>
      </div>
    );
  }

  return null;
}

// ---------------------------------------------------------------------------
// Page wrapper — shared chrome
// ---------------------------------------------------------------------------

function AdminMfaPageShell({ children }: { children: React.ReactNode }) {
  return (
    <div
      className="min-h-screen flex items-center justify-center bg-clinical-charcoal px-4"
      style={{ backgroundImage: 'radial-gradient(ellipse at 50% 0%, rgba(212,165,116,0.08) 0%, transparent 70%)' }}
    >
      <div className="w-full max-w-sm">
        {/* Branding */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-white/10 text-clinical-amber mb-4">
            <ShieldIcon />
          </div>
          <h1 className="font-heading text-2xl font-semibold text-white">Clinical Interpreter</h1>
          <p className="text-sm font-body text-white/50 mt-1 uppercase tracking-widest">Admin Portal</p>
        </div>

        <div className="bg-white rounded-2xl shadow-clinical-lg p-8">
          {children}
        </div>

        <p className="text-center text-xs font-body text-white/30 mt-6">
          Platform-level access only. Not for clinical use.
        </p>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Exported pages
// ---------------------------------------------------------------------------

export function AdminMfaResetRequestPage() {
  return (
    <AdminMfaPageShell>
      <RequestResetForm />
    </AdminMfaPageShell>
  );
}

export function AdminMfaResetConfirmPage() {
  const { token } = useParams<{ token: string }>();
  return (
    <AdminMfaPageShell>
      <ConfirmResetForm token={token ?? ''} />
    </AdminMfaPageShell>
  );
}
